import React, { useEffect, useId } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'

const fade = keyframes`from { opacity: 0 } to { opacity: 1 }`
const rise = keyframes`from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none }`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1400;
  animation: ${fade} 0.15s ease;
`
const Card = styled.div`
  width: 400px;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(12, 18, 41, 0.16);
  padding: 22px 22px 18px;
  animation: ${rise} 0.18s ease;
`
const Title = styled.h3`
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
`
const Message = styled.div`
  margin: 0 0 18px;
  font-size: 15px;
  line-height: 1.5;
  color: #374151;
`
const Foot = styled.div`display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 8px;`
const focusRing = `
  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`
const Ghost = styled.button`
  min-height: 40px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#475569'};
  font-weight: 600;
  font-size: 15px;
  padding: 9px 16px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'}; }
  ${focusRing}
`
const Confirm = styled.button`
  min-height: 40px;
  background: ${({ $tone, theme }) => ($tone === 'primary' ? '#33becc' : (theme?.colors?.error || '#d14925'))};
  color: ${({ $tone }) => ($tone === 'primary' ? '#0C1229' : '#fff')};
  border: none;
  font-weight: 700;
  font-size: 15px;
  padding: 9px 18px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { filter: brightness(1.05); }
  ${focusRing}
`

export default function ConfirmModal({ title, message, confirmLabel = 'Delete', cancelLabel = 'Cancel', tone = 'danger', onConfirm, onClose }) {
  const titleId = useId()

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const node = (
    <Overlay onMouseDown={onClose}>
      <Card role='dialog' aria-modal='true' aria-labelledby={titleId} onMouseDown={(e) => e.stopPropagation()}>
        <Title id={titleId}>{title}</Title>
        <Message>{message}</Message>
        <Foot>
          <Ghost type='button' onClick={onClose}>{cancelLabel}</Ghost>
          <Confirm type='button' $tone={tone} onClick={onConfirm} autoFocus>{confirmLabel}</Confirm>
        </Foot>
      </Card>
    </Overlay>
  )

  return typeof document !== 'undefined' ? createPortal(node, document.body) : node
}
